/**
 * The conversation title bar: which chat this is, with History behind the
 * name and New chat as the one button. Sits under the header (and, from
 * phase 6, under the Chat · Tender segments) so both are one tap away
 * without stealing the header from the tender.
 */
import {
  AuiIf,
  ThreadListItemMorePrimitive,
  ThreadListItemPrimitive,
  ThreadListPrimitive,
  useAuiState,
  useThreadListItemRuntime,
} from "@assistant-ui/react";
import { ArchiveIcon, ChevronDownIcon, MoreHorizontalIcon, PencilIcon, PlusIcon, TrashIcon } from "@/design/icons";
import { type FC, useEffect, useRef, useState } from "react";

import { useAgentsChat } from "@/agents-v2/components/common/chat-provider";
import { cn } from "@/agents-v2/utils/cn";
import { Popover, PopoverContent, PopoverTrigger } from "@/design/ui/popover";
import { Skeleton } from "@/design/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/design/ui/tooltip";

const NEW_CHAT = "New chat";

export const ConversationBar: FC = () => {
  const [historyOpen, setHistoryOpen] = useState(false);
  const listTitle = useAuiState((s) => s.threads.threadItems[s.threads.mainThreadId]?.title);
  // The list only re-reads on local changes; the metadata is refreshed after
  // each run, so the generated name shows up from there first.
  const { currentMetadata } = useAgentsChat();
  const metaTitle = typeof currentMetadata.title === "string" ? currentMetadata.title.trim() : "";
  const title = listTitle || metaTitle;
  const mainThreadId = useAuiState((s) => s.threads.mainThreadId);
  const count = useAuiState((s) => s.threads.threadIds.length);
  // Picking a conversation switches the main thread; close once it has.
  useEffect(() => {
    setHistoryOpen(false);
  }, [mainThreadId]);

  return (
    <div className="flex h-9 shrink-0 items-center gap-1 border-b border-line/70 pl-1 pr-1.5">
      <Popover open={historyOpen} onOpenChange={setHistoryOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Conversation history"
            className="flex min-w-0 flex-1 items-center gap-1 rounded-sm px-2 py-1 text-left transition-colors hover:bg-subtle data-[state=open]:bg-subtle"
          >
            <span className="truncate text-sm font-medium text-fg-2">{title || NEW_CHAT}</span>
            <ChevronDownIcon className="size-3 shrink-0 text-fg-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" sideOffset={2} className="w-[calc(100vw-16px)] max-w-sm p-0">
          <div className="flex items-center justify-between px-3 pb-1 pt-2.5">
            <span className="text-xs font-semibold text-fg-3">History</span>
            {count > 0 && <span className="text-xs text-fg-4">{count}</span>}
          </div>
          <ConversationHistory />
        </PopoverContent>
      </Popover>
      <Tooltip>
        <TooltipTrigger asChild>
          <ThreadListPrimitive.New asChild>
            <button
              type="button"
              aria-label={NEW_CHAT}
              className="flex size-7 shrink-0 items-center justify-center rounded-sm text-fg-2 transition-colors hover:bg-subtle hover:text-fg disabled:opacity-40"
            >
              <PlusIcon className="size-4" />
            </button>
          </ThreadListPrimitive.New>
        </TooltipTrigger>
        <TooltipContent side="bottom">{NEW_CHAT}</TooltipContent>
      </Tooltip>
    </div>
  );
};

/** The conversations of this document, most recent first. */
const ConversationHistory: FC = () => (
  <ThreadListPrimitive.Root className="max-h-[60vh] overflow-y-auto pb-1">
    <AuiIf condition={(s) => s.threads.isLoading}>
      <div className="flex flex-col gap-2 px-3 py-2">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-3.5 w-full" />
        ))}
      </div>
    </AuiIf>
    <AuiIf condition={(s) => !s.threads.isLoading && s.threads.threadIds.length === 0}>
      <div className="px-3 py-5 text-center text-sm text-fg-3">No conversations for this document yet.</div>
    </AuiIf>
    <AuiIf condition={(s) => !s.threads.isLoading}>
      <ThreadListPrimitive.Items>{() => <ConversationRow />}</ThreadListPrimitive.Items>
    </AuiIf>
  </ThreadListPrimitive.Root>
);

const ConversationRow: FC = () => {
  const runtime = useThreadListItemRuntime();
  const title = useAuiState((s) => s.threadListItem.title);
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const startRename = () => {
    setDraft(title || NEW_CHAT);
    setRenaming(true);
  };
  useEffect(() => {
    if (renaming) inputRef.current?.select();
  }, [renaming]);
  const commitRename = () => {
    const next = draft.trim();
    if (next && next !== title) void runtime.rename(next);
    setRenaming(false);
  };

  if (renaming) {
    return (
      <div className="px-2 py-0.5">
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitRename}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitRename();
            if (e.key === "Escape") setRenaming(false);
          }}
          aria-label="Conversation name"
          className="h-8 w-full rounded-sm border border-accent bg-surface px-2 text-sm outline-none"
        />
      </div>
    );
  }

  return (
    <ThreadListItemPrimitive.Root
      className={cn(
        "group mx-1 flex h-8 items-center rounded-sm transition-colors",
        "hover:bg-subtle focus-within:bg-subtle data-[active]:bg-accent-soft data-[active]:text-fg"
      )}
    >
      <ThreadListItemPrimitive.Trigger className="flex h-full min-w-0 flex-1 items-center px-2 text-left text-sm outline-none">
        <span className="min-w-0 flex-1 truncate">
          <ThreadListItemPrimitive.Title fallback={NEW_CHAT} />
        </span>
      </ThreadListItemPrimitive.Trigger>
      <ThreadListItemMorePrimitive.Root>
        <ThreadListItemMorePrimitive.Trigger asChild>
          <button
            type="button"
            aria-label="Conversation options"
            className="mr-1 flex size-6 items-center justify-center rounded-sm text-fg-3 opacity-0 transition-opacity hover:bg-hover hover:text-fg group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100 group-data-[active]:opacity-100"
          >
            <MoreHorizontalIcon className="size-3.5" />
          </button>
        </ThreadListItemMorePrimitive.Trigger>
        <ThreadListItemMorePrimitive.Content
          side="bottom"
          align="end"
          sideOffset={4}
          className="z-50 min-w-36 rounded-md border border-line bg-surface p-1 text-sm shadow-pop"
        >
          <ThreadListItemMorePrimitive.Item onSelect={startRename} className={menuItem}>
            <PencilIcon className="size-3.5" />
            Rename
          </ThreadListItemMorePrimitive.Item>
          <ThreadListItemPrimitive.Archive asChild>
            <ThreadListItemMorePrimitive.Item className={menuItem}>
              <ArchiveIcon className="size-3.5" />
              Archive
            </ThreadListItemMorePrimitive.Item>
          </ThreadListItemPrimitive.Archive>
          <ThreadListItemPrimitive.Delete asChild>
            <ThreadListItemMorePrimitive.Item
              className={cn(menuItem, "text-danger focus:bg-danger-soft focus:text-danger")}
            >
              <TrashIcon className="size-3.5" />
              Delete
            </ThreadListItemMorePrimitive.Item>
          </ThreadListItemPrimitive.Delete>
        </ThreadListItemMorePrimitive.Content>
      </ThreadListItemMorePrimitive.Root>
    </ThreadListItemPrimitive.Root>
  );
};

const menuItem =
  "flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 outline-none focus:bg-subtle focus:text-fg";
